import { stripUnwantedHtmlTags } from '@/utils/helpers/campaign.helper';

import {
  AI_EMAIL_LINK_CLASS,
  AI_EMAIL_PLACEHOLDER_CLASS,
  AI_EMAIL_SENDER_CLASS,
  AI_EMAIL_SOFT_BREAK_CLASS,
} from './ai-email-editor.constants';

export type AiEmailDraft = {
  subject: string;
  editorBodyHtml: string;
  bodyText: string;
  senderName?: string;
  linkTexts: string[];
  subjectTouched: boolean;
  bodyTouched: boolean;
  bodyEdited: boolean;
};

export type GeneratedAiEmail = {
  template: string;
  subject: string;
  senderName?: string;
};

const URL_PLACEHOLDER = '{{.URL}}';
const TRACKER = '{{.Tracker}}';
const HONEYPOT = '{{.HoneypotLink}}';
const NAME_PLACEHOLDER = /\{\{\.(FirstName|LastName)\}\}/g;

const PILL_SELECTOR = [
  AI_EMAIL_PLACEHOLDER_CLASS,
  AI_EMAIL_LINK_CLASS,
  AI_EMAIL_SENDER_CLASS,
]
  .map((className) => `.${className}`)
  .join(',');

const parseBody = (html: string): HTMLElement =>
  new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html').body;

export const splitEmailDocument = (
  template: string,
): { prefix: string; body: string; suffix: string } => {
  const open = /<body\b[^>]*>/i.exec(template);
  const closeAt =
    Array.from(template.matchAll(/<\/body>/gi)).pop()?.index ?? -1;

  if (!open && closeAt === -1)
    return { prefix: '', body: template, suffix: '' };

  const start = open ? open.index + open[0].length : 0;
  const end = closeAt === -1 ? template.length : closeAt;

  return {
    prefix: template.slice(0, start),
    body: template.slice(start, end),
    suffix: closeAt === -1 ? '' : template.slice(end),
  };
};

export const extractHiddenPlaceholders = (
  bodyHtml: string,
): { body: string; hidden: string[] } => {
  const hidden: string[] = [];
  const inOrder =
    /<[iI][mM][gG]\b[^>]*\{\{\.Tracker\}\}[^>]*>|\{\{\.Tracker\}\}|\{\{\.HoneypotLink\}\}/g;

  for (const [match] of bodyHtml.matchAll(inOrder)) {
    hidden.push(match === HONEYPOT ? HONEYPOT : TRACKER);
  }

  const body = bodyHtml
    .replace(
      /<(p|div)\b[^>]*>\s*(?:<img\b[^>]*\{\{\.Tracker\}\}[^>]*>|\{\{\.Tracker\}\}|\{\{\.HoneypotLink\}\})\s*<\/\1>/gi,
      '',
    )
    .replace(/<img\b[^>]*\{\{\.Tracker\}\}[^>]*>/gi, '')
    .replace(/\{\{\.Tracker\}\}/g, '')
    .replace(/\{\{\.HoneypotLink\}\}/g, '');

  return { body, hidden };
};

const textNodesOutsidePills = (root: HTMLElement): Text[] => {
  const walker = root.ownerDocument.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT,
  );
  const nodes: Text[] = [];

  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (!node.parentElement?.closest(PILL_SELECTOR)) nodes.push(node as Text);
  }

  return nodes;
};

const placeholderPill = (doc: Document, name: string): HTMLElement => {
  const span = doc.createElement('span');
  span.className = AI_EMAIL_PLACEHOLDER_CLASS;
  span.setAttribute('data-placeholder', name);
  return span;
};

export const toEditorHtml = (bodyHtml: string, senderName?: string): string => {
  const body = parseBody(bodyHtml);
  const doc = body.ownerDocument;

  body.querySelectorAll('style, script').forEach((el) => el.remove());

  body.querySelectorAll('a').forEach((anchor) => {
    if (anchor.getAttribute('href') === URL_PLACEHOLDER) {
      const pill = doc.createElement('a');
      pill.className = AI_EMAIL_LINK_CLASS;
      pill.appendChild(doc.createTextNode(anchor.textContent ?? ''));
      anchor.replaceWith(pill);
      return;
    }

    anchor.replaceWith(...Array.from(anchor.childNodes));
  });

  for (const node of textNodesOutsidePills(body)) {
    const text = node.nodeValue ?? '';
    const matches = Array.from(text.matchAll(NAME_PLACEHOLDER));
    if (matches.length === 0) continue;

    const fragment = doc.createDocumentFragment();
    let last = 0;

    for (const match of matches) {
      const at = match.index ?? 0;
      if (at > last)
        fragment.appendChild(doc.createTextNode(text.slice(last, at)));
      fragment.appendChild(placeholderPill(doc, match[1]));
      last = at + match[0].length;
    }

    if (last < text.length)
      fragment.appendChild(doc.createTextNode(text.slice(last)));
    node.replaceWith(fragment);
  }

  if (senderName) {
    const nodes = textNodesOutsidePills(body).reverse();

    for (const node of nodes) {
      const text = node.nodeValue ?? '';
      const at = text.lastIndexOf(senderName);
      if (at === -1) continue;

      const pill = doc.createElement('span');
      pill.className = AI_EMAIL_SENDER_CLASS;
      pill.appendChild(doc.createTextNode(senderName));

      const fragment = doc.createDocumentFragment();
      if (at > 0) fragment.appendChild(doc.createTextNode(text.slice(0, at)));
      fragment.appendChild(pill);
      const rest = text.slice(at + senderName.length);
      if (rest) fragment.appendChild(doc.createTextNode(rest));

      node.replaceWith(fragment);
      break;
    }
  }

  body.querySelectorAll('br').forEach((br) => {
    const parent = br.parentElement;
    if (!parent) return;

    const hasOther = Array.from(parent.childNodes).some((child) => {
      if (child === br) return false;
      if (child.nodeType === Node.TEXT_NODE)
        return (child.nodeValue ?? '').trim() !== '';
      return !(child instanceof HTMLBRElement);
    });

    if (hasOther) br.classList.add(AI_EMAIL_SOFT_BREAK_CLASS);
  });

  return body.innerHTML;
};

export const fromEditorHtml = (editorHtml: string): string => {
  const body = parseBody(editorHtml);
  const doc = body.ownerDocument;

  body.querySelectorAll(`.${AI_EMAIL_PLACEHOLDER_CLASS}`).forEach((el) => {
    const name = el.getAttribute('data-placeholder') ?? '';
    el.replaceWith(doc.createTextNode(`{{.${name}}}`));
  });

  body.querySelectorAll(`.${AI_EMAIL_LINK_CLASS}`).forEach((el) => {
    const anchor = doc.createElement('a');
    anchor.setAttribute('href', URL_PLACEHOLDER);
    anchor.appendChild(doc.createTextNode(el.textContent ?? ''));
    el.replaceWith(anchor);
  });

  body.querySelectorAll(`.${AI_EMAIL_SENDER_CLASS}`).forEach((el) => {
    el.replaceWith(doc.createTextNode(el.textContent ?? ''));
  });

  body.querySelectorAll(`br.${AI_EMAIL_SOFT_BREAK_CLASS}`).forEach((br) => {
    br.replaceWith(doc.createElement('br'));
  });

  body.querySelectorAll('span.ql-cursor').forEach((el) => {
    el.replaceWith(doc.createTextNode(el.textContent ?? ''));
  });

  return body.innerHTML.replace(/\uFEFF/g, '');
};

export const readDraftFromEditorHtml = (
  editorBodyHtml: string,
): { bodyText: string; senderName?: string; linkTexts: string[] } => {
  const body = parseBody(editorBodyHtml);

  const senderPill = body.querySelector(`.${AI_EMAIL_SENDER_CLASS}`);
  const senderName = senderPill ? senderPill.textContent ?? '' : undefined;
  const linkTexts = Array.from(
    body.querySelectorAll(`.${AI_EMAIL_LINK_CLASS}`),
  ).map((el) => el.textContent ?? '');

  body.querySelectorAll(PILL_SELECTOR).forEach((el) => el.remove());

  return { bodyText: body.textContent ?? '', senderName, linkTexts };
};

export const createAiEmailDraft = (
  generated: GeneratedAiEmail,
): AiEmailDraft => {
  const { body } = splitEmailDocument(generated.template);
  const { body: visibleBody } = extractHiddenPlaceholders(body);
  const safe = stripUnwantedHtmlTags(visibleBody);
  const editorBodyHtml = toEditorHtml(safe, generated.senderName);
  const { bodyText, senderName, linkTexts } =
    readDraftFromEditorHtml(editorBodyHtml);

  return {
    subject: generated.subject,
    editorBodyHtml,
    bodyText,
    senderName,
    linkTexts,
    subjectTouched: false,
    bodyTouched: false,
    bodyEdited: false,
  };
};

export const isAiEmailDraftValid = (draft: AiEmailDraft): boolean => {
  if (draft.subject.trim() === '') return false;
  if (draft.bodyText.trim() === '') return false;
  if (draft.linkTexts.some((text) => text.trim() === '')) return false;
  if (draft.senderName !== undefined && draft.senderName.trim() === '')
    return false;

  return true;
};

export const buildAiEmailPayload = (
  generated: GeneratedAiEmail,
  draft: AiEmailDraft,
): { emailTemplate: string; subject: string; senderName?: string } => {
  const { prefix, body, suffix } = splitEmailDocument(generated.template);
  const { hidden } = extractHiddenPlaceholders(body);

  const emailTemplate = draft.bodyEdited
    ? `${prefix}${fromEditorHtml(draft.editorBodyHtml)}${hidden.join(
        '',
      )}${suffix}`
    : generated.template;

  return {
    emailTemplate,
    subject: draft.subjectTouched ? draft.subject.trim() : generated.subject,
    senderName:
      draft.bodyEdited && draft.senderName !== undefined
        ? draft.senderName.trim()
        : generated.senderName,
  };
};
